import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  TextField,
  Button,
  Stack,
} from "@mui/material";
import { Calendar, Link21, DocumentUpload, DocumentText1 } from "iconsax-react";
import AIAgentDialog from "../PopupWindows/AIAgentDialog";
import { readStoredUser } from "../../utils/userSession";
import { formatLocalDate, formatLocalTime, parseDateTimeValue } from "../../utils/dateTime";
import { sanitizeImportedSourceText } from "../../utils/importTextSanitizers";
import {
  SIDEBAR_WIDGET_CARD_SX,
  SIDEBAR_WIDGET_TITLE_SX,
} from "./sidebarWidgetStyles";

const TITLE_BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const CARD_FRAME = "linear-gradient(135deg, #F13CFF 0%, #A45BFF 38%, #7065FF 68%, #39B8FF 100%)";
const SUBMIT_GRADIENT = "linear-gradient(90deg, #B655FF 0%, #8E4CFF 100%)";
const SUBMIT_GRADIENT_HOVER = "linear-gradient(90deg, #A847FF 0%, #7640F4 100%)";
const TEXT = "#344054";
const MUTED = "#A2ABB4";
const LINE = "#D2D8DE";
const LEGACY_TIME_LABEL = "Today at 12:28 AM";
const DEFAULT_PROMPT_TEXT = "Review this record and suggest next steps.";
const DEFAULT_LINK_TEXT = "https://www.linkedin.com/company/hourglass";
const DEFAULT_NOTE_TEXT = `Please see if everything is according to your
needs, if you want me to improve something,
just say the word :)!`;
const DEFAULT_ADDED_BY = "Rameet Singh";

function AIAgentSidebarIcon({ size = 24, color = TITLE_BLUE }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.5666 8.70373C14.9961 8.70373 16.1762 7.57481 16.2582 6.16408C16.2616 6.11285 16.2633 6.0599 16.2633 6.00696V4.24953H18.7927C19.4469 4.24953 19.9814 4.7841 19.9814 5.43823V12.801L21.4878 11.3903L22.6287 10.3211H21.4878V5.43994C21.4878 3.95577 20.2752 2.74316 18.7927 2.74316H15.4777C15.0815 2.74316 14.757 3.06766 14.757 3.4639V6.00696C14.757 6.66108 14.2224 7.19736 13.5666 7.19736H10.6051C9.95095 7.19736 9.41467 6.66279 9.41467 6.00696V5.75419H7.90831V6.00696C7.90831 7.48941 9.12091 8.70373 10.6051 8.70373H13.5666ZM12.0858 9.68748C13.348 9.68748 14.3727 10.7105 14.3727 11.9744C14.3727 13.2382 13.3497 14.2612 12.0858 14.2612C10.822 14.2612 9.79895 13.2382 9.79895 11.9744C9.79895 10.7105 10.822 9.68748 12.0858 9.68748ZM12.0858 11.1563C12.5367 11.1563 12.9022 11.5218 12.9022 11.9726C12.9022 12.4235 12.5367 12.789 12.0858 12.789C11.6349 12.789 11.2695 12.4235 11.2695 11.9726C11.2695 11.5218 11.6349 11.1563 12.0858 11.1563ZM10.6051 15.2433C9.17557 15.2433 7.99541 16.3722 7.91343 17.7829C7.91001 17.8342 7.90831 17.8871 7.90831 17.94V19.6992H5.37891C4.72478 19.6992 4.19021 19.1646 4.19021 18.5105V11.1477L2.68384 12.5585L1.54297 13.6259H2.68384V18.5088C2.68384 19.9912 3.89645 21.2055 5.38062 21.2055H8.69565C9.09188 21.2055 9.41638 20.881 9.41638 20.4848V17.9418C9.41638 17.2876 9.95095 16.7513 10.6068 16.7513H13.5683C14.2224 16.7513 14.7587 17.2859 14.7587 17.9418V18.1945H16.2651V17.9418C16.2651 16.4593 15.0524 15.245 13.5683 15.245H10.6068L10.6051 15.2433ZM17.2966 19.6975H18.791C19.4452 19.6975 19.9797 19.1629 19.9797 18.5088V16.1468C19.9695 16.1416 19.9609 16.1365 19.9507 16.1297L18.1369 15.0913L17.1446 14.5225C17.1446 14.5225 17.1378 14.5123 17.1344 14.5106C16.5998 14.1912 16.4 13.9794 16.3744 13.397H16.3709V12.0785C15.8312 11.8036 15.4623 11.2434 15.4623 10.5961C15.4623 9.67723 16.207 8.9343 17.1241 8.9343C18.0413 8.9343 18.7859 9.67894 18.7859 10.5961C18.7859 11.2434 18.417 11.8036 17.8773 12.0785V13.2023L18.499 13.5593L20.6902 14.8146C21.3119 15.1698 21.4844 15.3731 21.4861 16.1416V18.5088C21.4861 19.9912 20.2735 21.2055 18.7893 21.2055H16.9106H14.0977C12.6153 21.2055 11.401 19.9929 11.401 18.5088V18.256H12.9073V18.5088C12.9073 19.1629 13.4419 19.6992 14.0977 19.6992H17.2949L17.2966 19.6975ZM17.1241 9.87535C17.5204 9.87535 17.8432 10.1964 17.8432 10.5944C17.8432 10.9923 17.5221 11.3134 17.1241 11.3134C16.7262 11.3134 16.4051 10.9923 16.4051 10.5944C16.4051 10.1964 16.7262 9.87535 17.1241 9.87535ZM6.87503 4.24953H5.38062C4.72649 4.24953 4.19192 4.7841 4.19192 5.43823V7.80025C4.20217 7.80538 4.21071 7.8105 4.22095 7.81733L6.03474 8.85573L7.02703 9.42446C7.02703 9.42446 7.03386 9.43471 7.03728 9.43642C7.57185 9.75579 7.77167 9.96757 7.79729 10.55H7.80071V11.8685C8.3404 12.1434 8.70931 12.7036 8.70931 13.3509C8.70931 14.2698 7.96467 15.0127 7.04753 15.0127C6.13038 15.0127 5.38574 14.2681 5.38574 13.3509C5.38574 12.7036 5.75465 12.1434 6.29434 11.8685V10.7447L5.67267 10.3877L3.48143 9.13241C2.85976 8.77717 2.68726 8.57393 2.68555 7.80537V5.43994C2.68384 3.95577 3.89645 2.74316 5.38062 2.74316H7.25931H10.0722C11.5547 2.74316 12.7673 3.95577 12.7673 5.43994V5.6927H11.2609V5.43994C11.2609 4.78581 10.7263 4.24953 10.0705 4.24953H6.87503ZM7.04753 14.0717C6.65129 14.0717 6.3285 13.7506 6.3285 13.3526C6.3285 12.9547 6.64959 12.6336 7.04753 12.6336C7.44547 12.6336 7.76655 12.9547 7.76655 13.3526C7.76655 13.7506 7.44547 14.0717 7.04753 14.0717Z"
        fill={color}
      />
    </svg>
  );
}

const safeTrim = (value) => (value === undefined || value === null ? "" : String(value).trim());
const CARD_TIMESTAMP_KEYS = [
  "updated_at",
  "updatedAt",
  "last_activity_at",
  "lastActivityAt",
  "last_sync",
  "lastSync",
  "created_at",
  "createdAt",
  "added_at",
  "addedAt",
  "date_added",
  "dateAdded",
  "inserted_at",
  "insertedAt",
  "generated_at",
  "generatedAt",
  "timestamp",
];

function normalizeUrlCandidate(value = "") {
  const raw = safeTrim(value);
  if (!raw) return "";

  try {
    const normalized = raw.startsWith("http") ? raw : `https://${raw}`;
    return new URL(normalized).toString();
  } catch {
    return "";
  }
}

function isUrlLike(value = "") {
  return Boolean(normalizeUrlCandidate(value));
}

function resolveCardTimestamp(...sources) {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    for (const key of CARD_TIMESTAMP_KEYS) {
      const value = source[key];
      const text = safeTrim(value);
      if (text) return text;
    }
  }

  return "";
}

function isSameLocalDay(left, right) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function formatTimeLabel(value) {
  const date = parseDateTimeValue(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const timeText = formatLocalTime(date, {
    hour: "numeric",
    minute: "2-digit",
  });

  if (isSameLocalDay(date, now)) {
    return `Today at ${timeText}`;
  }

  const dateText = formatLocalDate(date, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });

  return `${dateText} at ${timeText}`;
}

export default function AIAgentCard({
  className,
  sx = {},
  title = "AI Agent",
  timeLabel,
  promptText = DEFAULT_PROMPT_TEXT,
  linkText = "https://www.linkedin.com/company/hourglass",
  addedBy = DEFAULT_ADDED_BY,
  noteText = DEFAULT_NOTE_TEXT,
  inputPlaceholder = "Continue...",
  companySeed = null,
  user,
  onUpload,
  onSubmit,
  currentJobId,
  currentCompanyId,
  currentCompanyName,
  entityType,
  entityId,
  entityName,
  contextText = "",
}) {
  const purpleIcon = { size: 18, color: PURPLE, variant: "Outline" };
  const [draft, setDraft] = useState("");
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [agentDialogInitialPrompt, setAgentDialogInitialPrompt] = useState("");
  const [openAttachmentPickerOnOpen, setOpenAttachmentPickerOnOpen] = useState(false);
  const [submitOnOpen, setSubmitOnOpen] = useState(false);
  const [localActivityAt, setLocalActivityAt] = useState("");
  const resolvedUser = useMemo(() => user || readStoredUser() || null, [user]);
  const explicitLinkProp = useMemo(
    () => (linkText !== DEFAULT_LINK_TEXT ? normalizeUrlCandidate(linkText) : ""),
    [linkText]
  );
  const resolvedCompanyLink = useMemo(
    () =>
      normalizeUrlCandidate(
        companySeed?.company_link ||
          companySeed?.website ||
          companySeed?.career_site ||
          explicitLinkProp
      ),
    [companySeed?.career_site, companySeed?.company_link, companySeed?.website, explicitLinkProp]
  );
  const resolvedCompanyName = useMemo(
    () => safeTrim(companySeed?.name),
    [companySeed?.name]
  );
  const resolvedNoteText = useMemo(
    () =>
      safeTrim(companySeed?.notes) ||
      "Create or refresh this company profile, scan the website for company information, leadership, team members, and current openings.",
    [companySeed?.notes]
  );
  const displayLinkText =
    resolvedCompanyLink || safeTrim(linkText === DEFAULT_LINK_TEXT ? "" : linkText);
  const displayLinkIsUrl = useMemo(() => isUrlLike(displayLinkText), [displayLinkText]);
  const displayLinkHref = useMemo(
    () => (displayLinkIsUrl ? normalizeUrlCandidate(displayLinkText) : ""),
    [displayLinkIsUrl, displayLinkText]
  );
  const displayPromptText = useMemo(() => {
    if (promptText !== DEFAULT_PROMPT_TEXT) return promptText;
    if (entityType && entityType !== "company") {
      return `Review ${entityName || `this ${entityType}`} and suggest next steps.`;
    }
    if (resolvedCompanyName) {
      return `Create or refresh the company profile for ${resolvedCompanyName}.`;
    }
    if (displayLinkText) {
      return "Create or refresh this company profile from the available public company link.";
    }
    return promptText;
  }, [displayLinkText, promptText, resolvedCompanyName, entityType, entityName]);
  const displayNote = useMemo(() => {
    if (noteText !== DEFAULT_NOTE_TEXT) return sanitizeImportedSourceText(noteText);
    if (entityType && entityType !== "company") return "Ask a question, request an update, or attach a file for the AI Agent to review.";
    return sanitizeImportedSourceText(resolvedNoteText);
  }, [noteText, resolvedNoteText, entityType]);
  const markClientActivityNow = useCallback((timestampValue = "") => {
    const nextValue = safeTrim(timestampValue) || new Date().toISOString();
    setLocalActivityAt(nextValue);
  }, []);
  const displayTimeLabel = useMemo(() => {
    if (localActivityAt) {
      return formatTimeLabel(localActivityAt) || LEGACY_TIME_LABEL;
    }

    const explicitTimeLabel = safeTrim(timeLabel);
    if (explicitTimeLabel && explicitTimeLabel !== LEGACY_TIME_LABEL) {
      return formatTimeLabel(explicitTimeLabel) || explicitTimeLabel;
    }

    const resolvedTimestamp = resolveCardTimestamp(companySeed) || new Date().toISOString();
    return formatTimeLabel(resolvedTimestamp) || LEGACY_TIME_LABEL;
  }, [companySeed, localActivityAt, timeLabel]);

  const recordType = entityType || (currentJobId ? "job" : "company");
  const recordId = entityId || (recordType === "job" ? currentJobId : recordType === "company" ? currentCompanyId || companySeed?.id : null);
  const recordName = entityName || currentCompanyName || resolvedCompanyName;
  useEffect(() => {
    setDraft("");
    setAgentDialogOpen(false);
    setLocalActivityAt("");
  }, [recordType, recordId, recordName]);

  const openAgentDialog = ({ openAttachmentPicker = false, submit = false } = {}) => {
    const request = safeTrim(draft) || (openAttachmentPicker
      ? "Analyze the attached files in the context of this record."
      : displayPromptText);
    const context = [
      `Current record: ${recordType}${recordId ? ` #${recordId}` : ""}${recordName ? ` (${recordName})` : ""}.`,
      currentCompanyId && recordType !== "company" ? `Related company ID: ${currentCompanyId}.` : "",
      displayLinkHref ? `Reference link: ${displayLinkHref}` : "",
      contextText,
    ].filter(Boolean).join("\n");
    setAgentDialogInitialPrompt(`${request}\n\nRecord context (reference data):\n${context}`);
    setOpenAttachmentPickerOnOpen(openAttachmentPicker);
    setSubmitOnOpen(submit);
    setAgentDialogOpen(true);
  };

  const handleUploadAction = () => {
    onUpload?.();
    openAgentDialog({ openAttachmentPicker: true });
  };

  const handleSubmitAction = () => {
    onSubmit?.(draft);
    openAgentDialog({ submit: true });
  };

  const handleAgentDialogClose = () => {
    setAgentDialogOpen(false);
    setOpenAttachmentPickerOnOpen(false);
    setSubmitOnOpen(false);
  };

  return (
    <>
    <Box
      className={className || "widget"}
      sx={{
        ...SIDEBAR_WIDGET_CARD_SX,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        border: "2px solid transparent",
        background:
          "linear-gradient(#FFFFFF, #FFFFFF) padding-box, " +
          `${CARD_FRAME} border-box`,
        boxShadow: "none",
        p: 4,
        ...sx,
      }}
    >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Typography
            className="heading"
            sx={{
              ...SIDEBAR_WIDGET_TITLE_SX,
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>

          <Box
            aria-hidden="true"
            sx={{
              width: 24,
              height: 24,
              display: "grid",
              placeItems: "center",
              color: TITLE_BLUE,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            <AIAgentSidebarIcon />
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: 1.6 }}>
          <Calendar size={18} color={MUTED} variant="Outline" />
          <Typography sx={{ color: MUTED, fontWeight: 400, fontSize: 14 }}>
            {displayTimeLabel}
          </Typography>
        </Box>

        <Typography
          sx={{
            mt: 1.8,
            color: TEXT,
            fontSize: 14,
            lineHeight: "24px",
            whiteSpace: "pre-line",
          }}
        >
          {displayPromptText}
        </Typography>

        {!!displayLinkText && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.4 }}>
            {displayLinkIsUrl ? (
              <Link21 {...purpleIcon} />
            ) : (
              <DocumentText1 size={18} color={PURPLE} variant="Outline" />
            )}
            <Typography
              component={displayLinkIsUrl ? "a" : "span"}
              href={displayLinkHref || undefined}
              target={displayLinkIsUrl ? "_blank" : undefined}
              rel={displayLinkIsUrl ? "noreferrer" : undefined}
              sx={{
                color: PURPLE,
                fontSize: 12,
                fontWeight: 400,
                textDecoration: "none",
                "&:hover": displayLinkIsUrl ? { textDecoration: "underline" } : undefined,
                wordBreak: "break-word",
              }}
            >
              {displayLinkText}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2.2, borderColor: LINE }} />

        <Typography
          sx={{
            color: TITLE_BLUE,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "24px",
            whiteSpace: "pre-line",
          }}
        >
          {displayNote}
        </Typography>

        <TextField
          placeholder={inputPlaceholder}
          fullWidth
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSubmitAction();
            }
          }}
          sx={{
            mt: 2.0,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              minHeight: 42,
              background: "#fff",
              "& fieldset": { borderColor: "#D1D5DB" },
              "&:hover fieldset": { borderColor: "#C7CED8" },
              "&.Mui-focused fieldset": {
                borderColor: "#A78BFA",
                borderWidth: 2,
              },
            },
            "& input": {
              fontSize: 14,
              color: TEXT,
              "&::placeholder": {
                color: MUTED,
                opacity: 1,
              },
            },
          }}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 2.2 }}
        >
          <Button
            onClick={handleUploadAction}
            variant="text"
            startIcon={<DocumentUpload {...purpleIcon} />}
            sx={{
              textTransform: "none",
              fontWeight: 400,
              color: PURPLE,
              fontSize: 14,
              px: 0.75,
              borderRadius: "999px",
              "&:hover": { background: "rgba(156,79,255,0.08)" },
            }}
          >
            Upload file
          </Button>

          <Button
            onClick={handleSubmitAction}
            variant="contained"
            disableElevation
            sx={{
              background: SUBMIT_GRADIENT,
              "&:hover": { background: SUBMIT_GRADIENT_HOVER },
              textTransform: "none",
              fontWeight: 400,
              fontSize: 12,
              borderRadius: "999px",
              height: 42,
              px: 2,
              minWidth: 74,
              boxShadow: "none",
            }}
          >
            Submit
          </Button>
        </Stack>
    </Box>

      <AIAgentDialog
        open={agentDialogOpen}
        onClose={handleAgentDialogClose}
        user={resolvedUser}
        onActivity={(activity) => {
          markClientActivityNow(activity?.createdAt);
          setDraft("");
          window.dispatchEvent(new Event("rpo:activities-refresh"));
        }}
        initialPrompt={agentDialogInitialPrompt}
        openAttachmentPickerOnOpen={openAttachmentPickerOnOpen}
        submitOnOpen={submitOnOpen}
      />

    </>
  );
}
